/**
 * A collection presentation's uploaded film (blocks/birrot-presentation-film.liquid,
 * docs/plans/completed/INLINE-VIDEO.md): plays muted while a quarter of it or more is
 * in view and pauses below that, so nothing plays off screen. Under reduced motion it stays on
 * its poster until Play. Pause / Play, Unmute / Mute and the progress bar
 * follow the video's own events, so the labels are right however it was
 * started or stopped.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video
 * @property {HTMLButtonElement} playButton
 * @property {HTMLButtonElement} soundButton
 * @property {HTMLElement} progress
 * @extends {Component<Refs>}
 */
class BirrotPresentationFilm extends Component {
  requiredRefs = ['video', 'playButton', 'soundButton', 'progress'];

  connectedCallback() {
    super.connectedCallback();
    // The viewer's Pause holds the film until their Play; scrolling it back
    // into view does not start it again.
    this.held = prefersReducedMotion();

    const { video } = this.refs;
    for (const type of ['play', 'pause', 'volumechange']) video.addEventListener(type, this.#label);
    video.addEventListener('timeupdate', this.#progress);

    // Both thresholds: with 0.25 alone a slow scroll out is seen at 20%,
    // still intersecting, and never again, so the film played on off screen.
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.25) video.pause();
        else if (!this.held) this.#play();
      },
      { threshold: [0, 0.25] }
    );
    this.observer.observe(this);
    this.#label();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { video } = this.refs;
    for (const type of ['play', 'pause', 'volumechange']) video.removeEventListener(type, this.#label);
    video.removeEventListener('timeupdate', this.#progress);
    this.observer?.disconnect();
  }

  togglePlay() {
    const { video } = this.refs;
    this.held = !video.paused;
    if (this.held) video.pause();
    else this.#play();
  }

  toggleSound() {
    const { video } = this.refs;
    video.muted = !video.muted;
    if (!video.muted && video.paused) {
      this.held = false;
      this.#play();
    }
  }

  #play() {
    // Refused (iOS Low Power Mode, a browser's autoplay rule): the poster
    // stays, with the Play label.
    this.refs.video.play().catch(this.#label);
  }

  #label = () => {
    const { video, playButton, soundButton } = this.refs;
    playButton.textContent = video.paused ? playButton.dataset.play : playButton.dataset.pause;
    soundButton.textContent = video.muted ? soundButton.dataset.unmute : soundButton.dataset.mute;
  };

  #progress = () => {
    const { currentTime, duration } = this.refs.video;
    const share = duration > 0 ? currentTime / duration : 0;
    this.refs.progress.style.setProperty('--birrot-film-progress', share.toFixed(4));
  };
}

if (!customElements.get('birrot-presentation-film')) {
  customElements.define('birrot-presentation-film', BirrotPresentationFilm);
}
